/**
 * EventTimeline - Horizontal visual timeline of detected/manual session events
 *
 * Shows all non-dismissed events as color-coded markers on a proportional time axis.
 * Clicking a marker calls onEventClick(timestamp) to navigate to that transcript location.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  parseTimestampToSeconds,
  normalizeTimestamp,
  formatSecondsToTimestamp,
} from '../lib/timestampUtils';

interface TimelineEvent {
  id: string;
  event_type: string;
  severity: 'critical' | 'warning' | 'positive' | 'info';
  title: string;
  start_time: string;
  context: string | null;
  competencies: string[] | null;
  confidence: number;
}

interface EventCluster {
  centerSeconds: number;
  events: TimelineEvent[];
  positionPct: number;
}

interface EventTimelineProps {
  sessionId: string;
  onEventClick?: (timestamp: string) => void;
  /** Increment to force re-fetch (e.g. after new detection run) */
  refreshTrigger?: number;
}

const SEVERITY_ORDER = ['critical', 'warning', 'positive', 'info'] as const;

const CLUSTERING_THRESHOLD_RATIO = 0.015; // 1.5% of total duration

function getHighestSeverity(events: TimelineEvent[]): string {
  for (const sev of SEVERITY_ORDER) {
    if (events.some((e) => e.severity === sev)) return sev;
  }
  return 'info';
}

const SEVERITY_CLASSES: Record<
  TimelineEvent['severity'],
  { bg: string; ring: string }
> = {
  critical: { bg: 'bg-red-500', ring: 'ring-red-300' },
  warning: { bg: 'bg-amber-400', ring: 'ring-amber-300' },
  positive: { bg: 'bg-green-500', ring: 'ring-green-300' },
  info: { bg: 'bg-sky-400', ring: 'ring-sky-300' },
};

function getSeverityBg(severity: string): string {
  return (SEVERITY_CLASSES[severity as TimelineEvent['severity']]?.bg ??
    SEVERITY_CLASSES.info.bg) as string;
}

function getSeverityRing(severity: string): string {
  return (SEVERITY_CLASSES[severity as TimelineEvent['severity']]?.ring ??
    SEVERITY_CLASSES.info.ring) as string;
}

function clusterEvents(events: TimelineEvent[], totalDuration: number): EventCluster[] {
  if (!events.length || totalDuration <= 0) return [];

  const threshold = totalDuration * CLUSTERING_THRESHOLD_RATIO;
  const clusters: EventCluster[] = [];

  for (const event of events) {
    const secs = parseTimestampToSeconds(event.start_time);
    const last = clusters[clusters.length - 1];

    if (last && Math.abs(secs - last.centerSeconds) <= threshold) {
      last.events.push(event);
      last.centerSeconds =
        last.events.reduce((sum, e) => sum + parseTimestampToSeconds(e.start_time), 0) /
        last.events.length;
    } else {
      clusters.push({ centerSeconds: secs, events: [event], positionPct: 0 });
    }
  }

  return clusters.map((c) => ({
    ...c,
    positionPct: Math.max(0, Math.min(100, (c.centerSeconds / totalDuration) * 100)),
  }));
}

export default function EventTimeline({
  sessionId,
  onEventClick,
  refreshTrigger,
}: EventTimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [totalDuration, setTotalDuration] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [eventsResult, transcriptsResult] = await Promise.all([
          supabase
            .from('events')
            .select('id, event_type, severity, title, start_time, context, competencies, confidence')
            .eq('session_id', sessionId)
            .eq('dismissed', false)
            .order('start_time'),
          supabase.from('transcripts').select('id').eq('session_id', sessionId),
        ]);

        if (cancelled) return;

        const fetchedEvents: TimelineEvent[] = eventsResult.data ?? [];
        setEvents(fetchedEvents);

        const getFallbackTotalDurationSeconds = () => {
          if (fetchedEvents.length === 0) return undefined;
          const maxSecs = Math.max(
            ...fetchedEvents.map((e) => parseTimestampToSeconds(e.start_time))
          );
          return maxSecs * 1.1;
        };

        // Derive total duration from speaker_segments MAX(end_ms)
        if (transcriptsResult.data && transcriptsResult.data.length > 0) {
          const ids = transcriptsResult.data.map((t: { id: string }) => t.id);
          const { data: lastSegment } = await supabase
            .from('speaker_segments')
            .select('end_ms')
            .in('transcript_id', ids)
            .order('end_ms', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (!cancelled) {
            if (lastSegment?.end_ms) {
              setTotalDuration(lastSegment.end_ms / 1000);
            } else {
              const fallbackTotalDuration = getFallbackTotalDurationSeconds();
              if (fallbackTotalDuration) setTotalDuration(fallbackTotalDuration);
            }
          }
        } else if (fetchedEvents.length > 0) {
          const fallbackTotalDuration = getFallbackTotalDurationSeconds();
          if (fallbackTotalDuration) setTotalDuration(fallbackTotalDuration);
        }
      } catch (err) {
        console.error('[EventTimeline] Error fetching data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshTrigger]);

  if (loading) {
    return (
      <div className="h-8 flex items-center px-1">
        <div className="w-full h-1.5 bg-gray-100 rounded-full animate-pulse" />
      </div>
    );
  }

  if (!events.length) {
    return (
      <p className="text-xs text-gray-400 py-2">No events detected yet.</p>
    );
  }

  const clusters = clusterEvents(events, totalDuration);
  type Stats = { total: number } & Record<TimelineEvent['severity'], number>;
  const stats = events.reduce<Stats>(
    (acc, event) => {
      acc[event.severity]++;
      return acc;
    },
    {
      total: events.length,
      critical: 0,
      warning: 0,
      positive: 0,
      info: 0,
    }
  );

  return (
    <div className="space-y-1.5">
      {/* Timeline bar + markers */}
      <div className="relative h-7 flex items-center">
        {/* Track */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 bg-gray-200 rounded-full" />

        {/* Event markers */}
        {clusters.map((cluster) => {
          const severity = getHighestSeverity(cluster.events);
          const isMultiple = cluster.events.length > 1;
          const clickTimestamp = cluster.events[0]!.start_time;

          return (
            <div
              key={cluster.events[0]!.id}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group/marker"
              style={{ left: `${cluster.positionPct}%` }}
            >
              <button
                type="button"
                disabled={!onEventClick}
                onClick={() => onEventClick?.(clickTimestamp)}
                className={[
                  'relative flex items-center justify-center rounded-full transition-transform duration-150',
                  isMultiple ? 'w-5 h-5' : 'w-3.5 h-3.5',
                  getSeverityBg(severity),
                  onEventClick
                    ? `cursor-pointer hover:scale-125 ring-2 ${getSeverityRing(severity)} ring-offset-1`
                    : 'cursor-default',
                ].join(' ')}
              >
                {isMultiple && (
                  <span className="text-white text-[9px] font-bold leading-none select-none">
                    {cluster.events.length}
                  </span>
                )}
              </button>

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 pointer-events-none opacity-0 group-hover/marker:opacity-100 transition-opacity duration-150">
                <div className="bg-gray-900 text-white text-xs rounded-md px-2.5 py-2 shadow-lg min-w-max max-w-xs">
                  <div className="font-semibold text-gray-200 mb-1">
                    {normalizeTimestamp(clickTimestamp)}
                  </div>
                  {cluster.events.slice(0, 3).map((e) => (
                    <div key={e.id} className="flex items-center gap-1.5">
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${getSeverityBg(e.severity)}`}
                      />
                      <span className="truncate max-w-[180px]">
                        {e.title || e.event_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                  {cluster.events.length > 3 && (
                    <div className="text-gray-400 mt-0.5">
                      +{cluster.events.length - 3} more
                    </div>
                  )}
                  {onEventClick && (
                    <div className="text-gray-400 mt-1 text-[10px]">Click to go to transcript</div>
                  )}
                </div>
                {/* Arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Time labels */}
      <div className="flex justify-between text-[10px] text-gray-400 px-0.5">
        <span>00:00:00</span>
        <span>{formatSecondsToTimestamp(totalDuration)}</span>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
        <span className="font-medium text-gray-700">{stats.total} events</span>
        {stats.critical > 0 && (
          <span className="text-red-600">{stats.critical} critical</span>
        )}
        {stats.warning > 0 && (
          <span className="text-amber-600">{stats.warning} warning</span>
        )}
        {stats.positive > 0 && (
          <span className="text-green-600">{stats.positive} positive</span>
        )}
        {stats.info > 0 && (
          <span className="text-sky-600">{stats.info} info</span>
        )}
      </div>
    </div>
  );
}
